import { Delaunay } from 'd3';
import type { ScalarGrid } from '../types';
import {
  EMPTY_MESH,
  packMesh,
  type MeshVertex,
  type SurfaceMesh,
} from './surfaceMesh';

export type AdaptiveMeshArgs = {
  grid: ScalarGrid;
  heightScale: number;
  /**
   * Stop once the worst remaining deviation is under this fraction of the field's range.
   *
   * A budget on fidelity rather than on triangle count, which is what makes the mesh adapt: a
   * field that is nearly flat reaches the tolerance almost immediately and stays cheap, while one
   * with sharp crests keeps earning vertices until its crests are resolved.
   */
  tolerance: number;
  /** Hard cap, so a pathological field cannot run away. */
  maximumVertices: number;
};

/**
 * Fraction of the outstanding candidates inserted per round.
 *
 * Garland and Heckbert insert the single worst point and re-triangulate, which converges beautifully
 * and costs a full triangulation per vertex. Taking a batch converges in a handful of rounds
 * instead of hundreds, at the cost of occasionally inserting a point that a previous insertion in
 * the same round would have made unnecessary - a few more vertices than strictly minimal, for
 * orders of magnitude less work.
 */
const BATCH_FRACTION = 0.35;

const MAXIMUM_ROUNDS = 24;

type Candidate = { column: number; row: number; value: number; error: number };

/**
 * The worst-approximated grid sample inside one triangle.
 *
 * Walks the grid cells the triangle covers and compares the field against the plane through the
 * triangle's three corners. This is the measure the refinement is greedy on, and it is what makes
 * vertices land on ridges without any explicit notion of a ridge: a crest is exactly where a plane
 * spanning its flanks deviates most from the surface.
 */
const findWorstSample = (
  grid: ScalarGrid,
  vertices: MeshVertex[],
  a: number,
  b: number,
  c: number
): Candidate | null => {
  const [pa, pb, pc] = [vertices[a], vertices[b], vertices[c]];

  const denominator =
    (pb.y - pc.y) * (pa.x - pc.x) + (pc.x - pb.x) * (pa.y - pc.y);
  if (denominator === 0) return null;

  const minColumn = Math.max(0, Math.ceil(Math.min(pa.x, pb.x, pc.x)));
  const maxColumn = Math.min(
    grid.width - 1,
    Math.floor(Math.max(pa.x, pb.x, pc.x))
  );
  const minRow = Math.max(0, Math.ceil(Math.min(pa.y, pb.y, pc.y)));
  const maxRow = Math.min(
    grid.height - 1,
    Math.floor(Math.max(pa.y, pb.y, pc.y))
  );

  let worst: Candidate | null = null;

  for (let row = minRow; row <= maxRow; row++) {
    for (let column = minColumn; column <= maxColumn; column++) {
      const value = grid.values[row * grid.width + column];
      if (value === null) continue;

      const weightA =
        ((pb.y - pc.y) * (column - pc.x) + (pc.x - pb.x) * (row - pc.y)) /
        denominator;
      const weightB =
        ((pc.y - pa.y) * (column - pc.x) + (pa.x - pc.x) * (row - pc.y)) /
        denominator;
      const weightC = 1 - weightA - weightB;
      if (weightA < 0 || weightB < 0 || weightC < 0) continue;

      const planeValue =
        weightA * pa.value + weightB * pb.value + weightC * pc.value;
      const error = Math.abs(value - planeValue);

      if (!worst || error > worst.error) {
        worst = { column, row, value, error };
      }
    }
  }

  return worst;
};

/**
 * Seeds the triangulation with the outline of the filled region.
 *
 * This has to contain every filled cell, and the reason is structural: a Delaunay triangulation
 * covers exactly the convex hull of its vertices, and refinement finds new points by walking the
 * triangles it already has. Anything outside the seed hull is therefore never visited, never
 * refined, and never drawn - the mesh is clipped to the seeds forever.
 *
 * Seeding from a coarse lattice, as this did before, quietly guaranteed that: a stride of sixteen
 * over sixty-four columns reaches column 48 and stops, so the mesh ended a quarter of the way short
 * of the data on two sides and the markers out there had no surface under them.
 *
 * Taking the first and last filled cell of every row and column gives an outline that provably
 * encloses the support; reducing it to its convex hull keeps the seed count small, since that is
 * the shape the triangulation was going to produce from it anyway.
 */
const findSeedVertices = (grid: ScalarGrid): MeshVertex[] => {
  const outline: MeshVertex[] = [];
  const seen = new Set<number>();

  const add = (column: number, row: number): void => {
    const index = row * grid.width + column;
    if (seen.has(index)) return;
    const value = grid.values[index];
    if (value === null) return;
    seen.add(index);
    outline.push({ x: column, y: row, value });
  };

  for (let row = 0; row < grid.height; row++) {
    let first = -1;
    let last = -1;
    for (let column = 0; column < grid.width; column++) {
      if (grid.values[row * grid.width + column] === null) continue;
      if (first < 0) first = column;
      last = column;
    }
    if (first < 0) continue;
    add(first, row);
    add(last, row);
  }

  for (let column = 0; column < grid.width; column++) {
    let first = -1;
    let last = -1;
    for (let row = 0; row < grid.height; row++) {
      if (grid.values[row * grid.width + column] === null) continue;
      if (first < 0) first = row;
      last = row;
    }
    if (first < 0) continue;
    add(column, first);
    add(column, last);
  }

  if (outline.length < 3) return outline;

  const hull = Array.from(
    Delaunay.from(
      outline,
      ({ x }) => x,
      ({ y }) => y
    ).hull
  ).map(index => outline[index]);

  if (hull.length < 3) return outline;

  /* Collinear runs dropped, keeping only the corners of the hull. A fully filled grid has a
     rectangular outline whose hull is every one of its ~250 boundary cells, and seeding all of them
     would spend the whole vertex budget describing four straight edges - on a plane, which needs
     four points total. Refinement will put vertices back along an edge if the field there earns
     them. */
  const corners = hull.filter((point, index) => {
    const previous = hull[(index - 1 + hull.length) % hull.length];
    const next = hull[(index + 1) % hull.length];
    const cross =
      (point.x - previous.x) * (next.y - previous.y) -
      (point.y - previous.y) * (next.x - previous.x);
    return Math.abs(cross) > 1e-9;
  });

  return corners.length >= 3 ? corners : hull;
};

const triangulate = (vertices: MeshVertex[]): Uint32Array =>
  Delaunay.from(
    vertices,
    ({ x }) => x,
    ({ y }) => y
  ).triangles as unknown as Uint32Array;

/**
 * Drops triangles whose centre falls outside the data's support.
 *
 * A Delaunay triangulation always covers the convex hull of its vertices, which will span holes and
 * concavities the field has no data for. Testing the centre against the grid keeps the mesh to
 * where components actually are, rather than roofing over the gaps.
 */
const keepSupportedTriangles = (
  grid: ScalarGrid,
  vertices: MeshVertex[],
  triangles: Uint32Array
): number[] => {
  const kept: number[] = [];

  for (let triangle = 0; triangle < triangles.length; triangle += 3) {
    const [a, b, c] = [
      triangles[triangle],
      triangles[triangle + 1],
      triangles[triangle + 2],
    ];
    const centreColumn = Math.round(
      (vertices[a].x + vertices[b].x + vertices[c].x) / 3
    );
    const centreRow = Math.round(
      (vertices[a].y + vertices[b].y + vertices[c].y) / 3
    );
    if (
      centreColumn < 0 ||
      centreRow < 0 ||
      centreColumn >= grid.width ||
      centreRow >= grid.height
    ) {
      continue;
    }
    if (grid.values[centreRow * grid.width + centreColumn] === null) continue;

    kept.push(a, b, c);
  }

  return kept;
};

/**
 * Builds a mesh whose vertex density follows the field's curvature.
 *
 * Greedy error-driven insertion, after Garland and Heckbert: triangulate what you have, find where
 * the resulting piecewise-linear surface deviates most from the field, insert there, repeat. No
 * ridge detection is involved - a crest is simply where a plane spanning its flanks is most wrong,
 * so it attracts vertices on its own. That is the difference from a uniform lattice, which spends
 * the same number of triangles on a flat plain as on a knife edge and resolves neither well: the
 * plain is over-sampled and the crest still cuts the corner.
 */
export const toAdaptiveMesh = ({
  grid,
  heightScale,
  tolerance,
  maximumVertices,
}: AdaptiveMeshArgs): SurfaceMesh => {
  if (grid.width < 2 || grid.height < 2) return EMPTY_MESH;

  const vertices = findSeedVertices(grid);
  if (vertices.length < 3) return EMPTY_MESH;

  const [zMin, zMax] = grid.zRange;
  const absoluteTolerance = tolerance * (zMax - zMin || 1);

  let triangles = triangulate(vertices);

  for (let round = 0; round < MAXIMUM_ROUNDS; round++) {
    if (vertices.length >= maximumVertices) break;

    const candidates: Candidate[] = [];
    const alreadyPlaced = new Set(
      vertices.map(({ x, y }) => y * grid.width + x)
    );

    for (let triangle = 0; triangle < triangles.length; triangle += 3) {
      const worst = findWorstSample(
        grid,
        vertices,
        triangles[triangle],
        triangles[triangle + 1],
        triangles[triangle + 2]
      );
      if (!worst || worst.error <= absoluteTolerance) continue;
      if (alreadyPlaced.has(worst.row * grid.width + worst.column)) continue;
      candidates.push(worst);
    }

    if (candidates.length === 0) break;

    candidates.sort((left, right) => right.error - left.error);
    const room = maximumVertices - vertices.length;
    const take = Math.max(
      1,
      Math.min(room, Math.ceil(candidates.length * BATCH_FRACTION))
    );

    const inserted = new Set<number>();
    for (const candidate of candidates.slice(0, take)) {
      const key = candidate.row * grid.width + candidate.column;
      /* Two triangles can nominate the same sample, and inserting it twice would give the
         triangulation a duplicate point and a zero-area triangle. */
      if (inserted.has(key)) continue;
      inserted.add(key);
      vertices.push({
        x: candidate.column,
        y: candidate.row,
        value: candidate.value,
      });
    }

    triangles = triangulate(vertices);
  }

  return packMesh({
    vertices,
    indices: keepSupportedTriangles(grid, vertices, triangles),
    grid,
    heightScale,
  });
};
